import React, { useState, useRef, Suspense, useEffect } from "react";
import { productsUrl } from "../../api";
import { toast } from "react-toastify";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Canvas, useLoader, useThree } from '@react-three/fiber';
import { OrbitControls, Stage, useGLTF, Html, Center } from '@react-three/drei';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import * as THREE from 'three';

// Component to handle model loading and rendering
// Sub-component for STL files
const STLModel = ({ url }) => {
    const geometry = useLoader(STLLoader, url);
    return (
        <mesh geometry={geometry}>
            <meshStandardMaterial color="gray" />
        </mesh>
    );
};

// Sub-component for GLB/GLTF files
const GLTFModel = ({ url }) => {
    const { scene } = useGLTF(url);
    return <primitive object={scene} />;
};

// Main Model Dispatcher
const Model = ({ url, fileName, onLoaded }) => {
    // Check extension from fileName if available, otherwise fallback to url
    const nameToCheck = fileName || url;
    const isStl = nameToCheck.toLowerCase().endsWith('.stl');
    const { gl } = useThree();

    useEffect(() => {
        if (onLoaded) onLoaded(gl);
    }, [onLoaded, gl]);

    if (isStl) return <STLModel url={url} />;
    return <GLTFModel url={url} />;
};

const AddProduct = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dropId = searchParams.get('dropId');
  const [loading, setLoading] = useState(false);
  
  // Refs for form inputs
  const titleRef = useRef();
  const authorRef = useRef();
  const currentPriceRef = useRef();
  const previousPriceRef = useRef();
  const descriptionRef = useRef();
  
  const [coverImage, setCoverImage] = useState(null);
    const [galleryFiles, setGalleryFiles] = useState([]); // New state for gallery
  const [modelFile, setModelFile] = useState(null);
  const [modelPreviewUrl, setModelPreviewUrl] = useState(null);
  const [modelFileName, setModelFileName] = useState("");
  const [generatedThumbnail, setGeneratedThumbnail] = useState(null);
  const canvasRef = useRef();

  const handleModelSelect = (e) => {
      const file = e.target.files[0];
      if (file) {
          setModelFile(file);
          setModelFileName(file.name);
          const url = URL.createObjectURL(file);
          setModelPreviewUrl(url);
      }
  };

    const handleCaptureThumbnail = () => {
    const canvas = document.querySelector('canvas');
    if (canvas) {
        const dataUrl = canvas.toDataURL('image/png');
        setGeneratedThumbnail(dataUrl);
        fetch(dataUrl)
            .then(res => res.blob())
            .then(blob => {
                const file = new File([blob], "thumbnail.png", { type: "image/png" });
                setCoverImage(file);
                toast.success("Thumbnail generated and set as Cover Image!");
            });
    }
  };

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('modelFile', file);
    
    // Determine endpoint based on file type or generic upload
    const res = await fetch('https://backend.cdp3d.com/api/upload-model', {
        method: 'POST',
        body: formData
    });
    const data = await res.json();
    return data.filePath;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
        let coverImageUrl = "";
        let modelFileUrl = "";
        let galleryUrls = [];

        if (coverImage) {
            coverImageUrl = await uploadFile(coverImage);
        }
        
        if (modelFile) {
            modelFileUrl = await uploadFile(modelFile);
        }

        // Upload Gallery Images
        if (galleryFiles.length > 0) {
            const uploadPromises = galleryFiles.map(file => uploadFile(file));
            galleryUrls = await Promise.all(uploadPromises);
        }

        const productData = {
            title: titleRef.current.value,
            additionalDetails: { author: authorRef.current.value },
            currentPrice: currentPriceRef.current.value,
            previousPrice: previousPriceRef.current.value,
            description: descriptionRef.current.value,
            coverImage: coverImageUrl,
            images: galleryUrls, // Add images array
            modelUrl: modelFileUrl,
            availableQuantity: 100, // default
            dropId: dropId || null
        };

        const res = await fetch(productsUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(productData)
        });

        if (res.ok) {
            toast.success("Product Created Successfully!");
            navigate("/products");
        } else {
            toast.error("Failed to create product");
        }

    } catch (error) {
        console.error(error);
        toast.error("Error creating product");
    } finally {
        setLoading(false);
    }
  };

  return (
    <div className="content-wrapper">
        <h2 className="mb-4">
            Add New Product
            {dropId && <span className="badge bg-info ms-2 fs-6">Adding to Drop</span>}
        </h2>
        <div className="row">
            <div className="col-md-8">
                <form onSubmit={handleSubmit} className="container bg-white p-4 rounded shadow">
                    <div className="mb-3">
                        <label className="form-label">Title</label>
                        <input ref={titleRef} className="form-control" required />
                    </div>

                    <div className="mb-3">
                        <label className="form-label">Designer / Author</label>
                        <input ref={authorRef} className="form-control" />
                    </div>

                    <div className="row mb-3">
                        <div className="col">
                            <label className="form-label">Current Price</label>
                            <input ref={currentPriceRef} type="number" className="form-control" required />
                        </div>
                        <div className="col">
                            <label className="form-label">Previous Price</label>
                            <input ref={previousPriceRef} type="number" className="form-control" />
                        </div>
                    </div>

                    <div className="mb-3">
                        <label className="form-label">Description</label>
                        <textarea ref={descriptionRef} className="form-control" rows="3"></textarea>
                    </div>

                    <div className="mb-3">
                        <label className="form-label">3D Model (.glb, .gltf, .stl)</label>
                        <input type="file" className="form-control" onChange={handleModelSelect} accept=".glb,.gltf,.stl" />
                        <div className="form-text">Select a model to preview and generate thumbnail.</div>
                    </div>

                    <div className="mb-3">
                        <label className="form-label">Cover Image</label>
                        <input type="file" className="form-control" onChange={(e) => setCoverImage(e.target.files[0])} accept="image/*" />
                        <div className="form-text">Upload manually or generate from 3D preview.</div>
                        {generatedThumbnail && <img src={generatedThumbnail} alt="Thumbnail" className="mt-2 img-thumbnail" style={{maxHeight: '100px'}} />}
                    </div>

                      <div className="mb-3">
                          <label className="form-label">Product Gallery (Optional)</label>
                          <input
                              type="file"
                              className="form-control"
                              onChange={(e) => setGalleryFiles(Array.from(e.target.files))}
                              accept="image/*"
                              multiple
                          />
                          <div className="form-text">Select multiple images for the product carousel.</div>
                          {galleryFiles.length > 0 && (
                              <div className="mt-2 d-flex gap-2 flex-wrap">
                                  {galleryFiles.map((file, i) => (
                                      <span key={i} className="badge bg-secondary">{file.name}</span>
                                  ))}
                              </div>
                          )}
                      </div>

                    <button type="submit" className="btn btn-primary" disabled={loading}>
                        {loading ? "Creating..." : "Create Product"}
                    </button>
                </form>
            </div>
            
            <div className="col-md-4">
                {modelPreviewUrl && (
                    <div className="card shadow sticky-top" style={{top: '20px'}}>
                        <div className="card-header bg-dark text-white">3D Preview</div>
                        <div className="card-body p-0" style={{height: '300px'}}>
                            <Canvas gl={{ preserveDrawingBuffer: true }} camera={{ fov: 50 }}>
                                <Suspense fallback={<Html center>Loading...</Html>}>
                                    <Stage environment="city" intensity={0.6}>
                                        <Model url={modelPreviewUrl} fileName={modelFileName} />
                                    </Stage>
                                </Suspense>
                                <OrbitControls autoRotate />
                            </Canvas>
                        </div>
                        <div className="card-footer text-center">
                            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleCaptureThumbnail}>
                                <i className="bi bi-camera me-1"></i> Capture Thumbnail
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    </div>
  );
};

export default AddProduct;
